import { useEffect, useState } from "react";
import api from "../../../../../utils/axiosInstance";
import DocumentsTable from "./DocumentsTable";
import DocumentUpload from "./DocumentUpload";
import ConfirmModal from "../../../../../components/Modals/ConfirmModal";
import { useToast } from "../../../../../components/Toast/ToastProvider";

const DocumentsPage = ({ clientId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, docId: null, docName: "" });
  const toast = useToast();

  const fetchDocuments = async () => {
    try {
      const res = await api.get(`/client-documents?client_id=${clientId}`, {
        withCredentials: true,
      });
      setDocuments(res.data.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Failed to fetch documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [clientId]);

  const handleDeleteClick = (id, name) => {
    setDeleteModal({ show: true, docId: id, docName: name });
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/client-documents/${deleteModal.docId}`, { withCredentials: true });
      setDocuments((prev) => prev.filter((doc) => doc.id !== deleteModal.docId));
      setDeleteModal({ show: false, docId: null, docName: "" });
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete document");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ show: false, docId: null, docName: "" });
  };

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    fetchDocuments();
  };

  return (
    <div className="space-y-5">
      <section>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Client Documents
          </h3>
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
            >
              Upload Document
            </button>
            <button
              onClick={() => toast.info("Add URL feature coming soon!")}
              className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded"
            >
              Add URL
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-sm text-gray-500 py-10">Loading documents...</div>
        ) : (
          <DocumentsTable documents={documents} onDelete={handleDeleteClick} />
        )}
      </section>

      {showUploadModal && (
        <DocumentUpload
          clientId={clientId}
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
        />
      )}

      {deleteModal.show && (
        <ConfirmModal
          title="Delete Document"
          message={`Are you sure you want to delete "${deleteModal.docName}"? This action cannot be undone.`}
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          confirmText="Delete"
          confirmColor="bg-red-600"
          confirmHoverColor="hover:bg-red-700"
        />
      )}
    </div>
  );
};

export default DocumentsPage;
